import React from 'react'
import {
    Row,
    Column,
    WrapRow,
    RatioContainer,
    SectionTitle,
    SectionSeparator,
} from '../../shared/shared.styled'
import { useIsAdmin } from '../../shared/hooks/use-is-admin.hook'
import {
    AnalyticValues,
    AnalyticCharts,
    SimpleUser,
    Supplier,
} from '../../shared/shared.models'
import { euroFormatter } from '../../shared/shared.utils'
import styled from 'styled-components'
import { Typography, Colors } from '../../configs/styled.config'
import TranslationUtils from '../../shared/utils/translations.utils'
import { Tabs, AppBar, Tab } from '@mui/material'
import { TabPanel } from '../../shared/components/tabs/tabs-tab-panel.component'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import {
    AreaChart,
    Area,
    ResponsiveContainer,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
} from 'recharts'
import Dropdown from '../../shared/components/dropdown.component'
import { DropdownUtils } from '../../shared/utils/dropdowns.utils'
import { useSelector } from 'react-redux'
import { IStoreInterface } from '../../configs/store.config'
import { AdminApi } from '../admin.api'
import { useAlert } from 'react-alert'
import CompanySuppliers from './company-suppliers.component'
import { useIsEditor3 } from '../../shared/hooks/use-is-editor.hook'

interface AnalyticValuesProps {
    analytics?: AnalyticValues
    contactsChartData?: AnalyticCharts[]
    isLoadingContacts: boolean
    companyUsers: SimpleUser[]
    filterUser: string
    setFilterUser: (value: string) => void
    filterInitialDate: string
    setFilterInitialDate: (value: string) => void
    filterEndDate: string
    setFilterEndDate: (value: string) => void
    filterGroupBy: string
    setFilterGroupBy: (value: string) => void
}

const ACCENT = Colors['primary-digiauto']

export const Analytics = ({
    analytics,
    contactsChartData,
    isLoadingContacts,
    companyUsers,
    filterUser,
    setFilterUser,
    filterInitialDate,
    setFilterInitialDate,
    filterEndDate,
    setFilterEndDate,
    filterGroupBy,
    setFilterGroupBy,
}: AnalyticValuesProps) => {
    // Hooks initialization
    const isAdmin = useIsAdmin()
    const isEditor3 = useIsEditor3()
    const alert = useAlert()
    const [suppliers, setSuppliers] = React.useState<Supplier[]>([])

    const tabsAdmin = [
        { id: 'analytics', name: 'Análise' },
        { id: 'supplier-management', name: 'Gestão de Fornecedores' },
    ]
    const tabsEditor = [
        { id: 'supplier-management', name: 'Gestão de Fornecedores' },
    ]

    const groupByOpts = [
        { id: '', name: 'Dia' },
        { id: 'month', name: 'Mês' },
        { id: 'year', name: 'Ano' },
    ]

    const [tabSelected, setTabSelected] = React.useState(0)
    const [isLoadingSuppliers, setIsLoadingSuppliers] = React.useState(false)

    const { company } = useSelector(
        (state: IStoreInterface) => state.adminReducer
    )

    React.useEffect(() => {
        try {
            setIsLoadingSuppliers(true)
            if (company) {
                AdminApi.methods.getSuppliers(company.uuid).then(
                    (res) => {
                        setSuppliers(res.data.suppliers)
                        setIsLoadingSuppliers(false)
                    },
                    (e) => {
                        alert.error(
                            'Erro ao listar os fornecedores. Por favor tente mais tarde'
                        )
                    }
                )
            }
        } catch (e) {
            alert.error(
                'Erro ao listar os fornecedores. Por favor tente mais tarde'
            )
        }
    }, [alert, company])

    const theme = createTheme({
        components: {
            MuiAppBar: {
                styleOverrides: {
                    colorPrimary: {
                        backgroundColor: 'transparent', // Transparent background for AppBar
                        borderBottom: '1px solid #e8e8e8',
                        boxShadow: 'none',
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        backgroundColor: ACCENT,
                        height: 3,
                        borderRadius: 3,
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        // Custom styling for the Tab label
                        '& .MuiTab-label': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                        },
                        '&.Mui-selected': {
                            color: ACCENT,
                        },
                    },
                },
            },
        },
    })

    function a11yProps(index: number) {
        return {
            id: `management-tab-${index}`,
            'aria-controls': `management-tabpanel-${index}`,
        }
    }

    const tabs = isAdmin ? tabsAdmin : isEditor3 ? tabsEditor : []
    const analyticsTabIndex = tabs.findIndex((tab) => tab.id === 'analytics')
    const supplierTabIndex = tabs.findIndex(
        (tab) => tab.id === 'supplier-management'
    )

    const ageBuckets = [
        {
            label: 'Menos de 6 meses',
            count: analytics?.carLessSixMonths ?? 0,
            color: `${ACCENT}4d`,
        },
        {
            label: 'Entre 6 e 12 meses',
            count: analytics?.carSixToTwelveMonths ?? 0,
            color: `${ACCENT}99`,
        },
        {
            label: 'Mais de 12 meses',
            count: analytics?.carMoreTwelveMonths ?? 0,
            color: ACCENT,
        },
    ]

    const fuelItems = (analytics?.numberCarsFuel ?? [])
        .map((item) => ({
            label: TranslationUtils.getFuelTypeTranslation(item.typeAnalysis),
            count: item.count,
        }))
        .filter((item) => item.count > 0)

    const brandItems = (analytics?.numberCarsBrand ?? [])
        .map((item) => ({ label: item.typeAnalysis, count: item.count }))
        .filter((item) => item.count > 0)

    const standItems = (analytics?.numberCarsStand ?? [])
        .map((item) => ({ label: item.typeAnalysis, count: item.count }))
        .filter((item) => item.count > 0)

    const hasContactsData = (contactsChartData?.length ?? 0) > 0

    return isAdmin || isEditor3 ? (
        <ThemeProvider theme={theme}>
            <AppBar position="static">
                <Tabs
                    value={tabSelected}
                    onChange={(event, newValue) => setTabSelected(newValue)}
                    aria-label="management tabs"
                >
                    {tabs?.map((elem, index) => (
                        <Tab
                            label={<TabsRowLabel>{elem?.name}</TabsRowLabel>}
                            {...a11yProps(index)}
                        />
                    ))}
                </Tabs>
            </AppBar>
            {isAdmin && (
                <TabPanel value={tabSelected} index={analyticsTabIndex}>
                    <SectionTitle>Análise de Stock</SectionTitle>
                    <StatGrid>
                        <StatTile>
                            <StatLabel>Valor total do stock</StatLabel>
                            <StatValue>
                                {euroFormatter.format(
                                    analytics ? analytics.totalValueStock : 0
                                )}
                            </StatValue>
                        </StatTile>
                        <StatTile>
                            <StatLabel>Margem média do stock</StatLabel>
                            <StatValue>
                                {euroFormatter.format(
                                    analytics ? analytics.averageStockMargin : 0
                                )}
                            </StatValue>
                        </StatTile>
                        <StatTile>
                            <StatLabel>Carros em stock</StatLabel>
                            <StatValue>
                                {analytics?.numberCarsStock ?? 0}
                            </StatValue>
                        </StatTile>
                        <StatTile>
                            <StatLabel>Tempo médio em stock</StatLabel>
                            <StatValue>
                                {Math.floor(
                                    analytics ? analytics?.averageStockTime : 0
                                )}{' '}
                                <StatUnit>dias</StatUnit>
                            </StatValue>
                        </StatTile>
                    </StatGrid>

                    <SectionSeparator />

                    <SectionTitle>Gestão de Carros</SectionTitle>
                    <BreakdownGrid>
                        <BreakdownCard>
                            <BreakdownTitle>Idade do stock</BreakdownTitle>
                            <StackedBar>
                                {ageBuckets.map((bucket) => {
                                    return (
                                        <StackedBarSegment
                                            key={bucket.label}
                                            style={{
                                                flexGrow: bucket.count,
                                                flexBasis: 0,
                                                backgroundColor: bucket.color,
                                                display:
                                                    bucket.count > 0
                                                        ? 'block'
                                                        : 'none',
                                            }}
                                            title={`${bucket.label}: ${bucket.count}`}
                                        />
                                    )
                                })}
                            </StackedBar>
                            <StackedBarLegend>
                                {ageBuckets.map((bucket) => (
                                    <LegendItem key={bucket.label}>
                                        <LegendSwatch
                                            style={{
                                                backgroundColor: bucket.color,
                                            }}
                                        />
                                        <LegendLabel>
                                            {bucket.label}
                                        </LegendLabel>
                                        <LegendCount>
                                            {bucket.count}
                                        </LegendCount>
                                    </LegendItem>
                                ))}
                            </StackedBarLegend>
                        </BreakdownCard>

                        <BreakdownCard>
                            <BreakdownTitle>
                                Carros por combustível
                            </BreakdownTitle>
                            <BarList items={fuelItems} />
                        </BreakdownCard>

                        <BreakdownCard>
                            <BreakdownTitle>Carros por marca</BreakdownTitle>
                            <BarList items={brandItems} />
                        </BreakdownCard>

                        <BreakdownCard>
                            <BreakdownTitle>Carros por stand</BreakdownTitle>
                            <BarList items={standItems} />
                        </BreakdownCard>
                    </BreakdownGrid>

                    <SectionSeparator />

                    <SectionTitle>Gestão de Contatos</SectionTitle>
                    <SearchContainer>
                        <WrapRow>
                            <RatioContainer ratio={2 / 7}>
                                <Row>
                                    <StyledInput
                                        value={
                                            filterInitialDate
                                                ? filterInitialDate
                                                : ''
                                        }
                                        type="date"
                                        placeholder="Data Inicial"
                                        onChange={(e) =>
                                            setFilterInitialDate(e.target.value)
                                        }
                                    />
                                    <DateSeparator>-</DateSeparator>
                                    <StyledInput
                                        value={
                                            filterEndDate ? filterEndDate : ''
                                        }
                                        type="date"
                                        placeholder="Data Final"
                                        onChange={(e) =>
                                            setFilterEndDate(e.target.value)
                                        }
                                    />
                                </Row>
                            </RatioContainer>
                            <RatioContainer ratio={1 / 7}>
                                <Dropdown
                                    placeholder="Utilizador"
                                    selectValue={(val) =>
                                        setFilterUser(val.key ?? '')
                                    }
                                    value={filterUser}
                                    clearValue={() => setFilterUser('')}
                                    options={DropdownUtils.getAllCompanyUsersDropdownOptions(
                                        companyUsers
                                    )}
                                />
                            </RatioContainer>
                            <RatioContainer ratio={1 / 7}>
                                <Row>
                                    <FilterLabel>Agrupar</FilterLabel>
                                    <GroupByGroup role="group">
                                        {groupByOpts.map((group) => (
                                            <GroupByButton
                                                key={`filter-group-${group.id}`}
                                                type="button"
                                                active={
                                                    filterGroupBy === group.id
                                                }
                                                onClick={() =>
                                                    setFilterGroupBy(group.id)
                                                }
                                            >
                                                {group.name}
                                            </GroupByButton>
                                        ))}
                                    </GroupByGroup>
                                </Row>
                            </RatioContainer>
                        </WrapRow>
                    </SearchContainer>

                    <ChartCard>
                        <ChartHeading>Novos contactos por dia</ChartHeading>
                        {hasContactsData ? (
                            <ChartWrapper
                                style={{
                                    opacity: isLoadingContacts ? 0.45 : 1,
                                }}
                            >
                                <ResponsiveContainer width="100%" height={320}>
                                    <AreaChart
                                        data={contactsChartData}
                                        margin={{
                                            top: 8,
                                            right: 16,
                                            left: 0,
                                            bottom: 0,
                                        }}
                                    >
                                        <defs>
                                            <linearGradient
                                                id="contactsFill"
                                                x1="0"
                                                y1="0"
                                                x2="0"
                                                y2="1"
                                            >
                                                <stop
                                                    offset="5%"
                                                    stopColor={ACCENT}
                                                    stopOpacity={0.28}
                                                />
                                                <stop
                                                    offset="95%"
                                                    stopColor={ACCENT}
                                                    stopOpacity={0.02}
                                                />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid
                                            vertical={false}
                                            stroke={Colors['ice-blue']}
                                        />
                                        <XAxis
                                            dataKey="date"
                                            tick={{
                                                fontSize: 12,
                                                fill: Colors['light-grey-blue'],
                                            }}
                                            axisLine={{
                                                stroke: Colors['ice-blue'],
                                            }}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            allowDecimals={false}
                                            width={32}
                                            tick={{
                                                fontSize: 12,
                                                fill: Colors['light-grey-blue'],
                                            }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <Tooltip
                                            content={<ContactsTooltip />}
                                            cursor={{
                                                stroke: Colors[
                                                    'light-grey-blue'
                                                ],
                                                strokeWidth: 1,
                                            }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="value"
                                            stroke={ACCENT}
                                            strokeWidth={2}
                                            fill="url(#contactsFill)"
                                            dot={{
                                                r: 4,
                                                fill: ACCENT,
                                                stroke: Colors.white,
                                                strokeWidth: 2,
                                            }}
                                            activeDot={{
                                                r: 6,
                                                fill: ACCENT,
                                                stroke: Colors.white,
                                                strokeWidth: 2,
                                            }}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </ChartWrapper>
                        ) : (
                            <EmptyChartState>
                                {isLoadingContacts
                                    ? 'A carregar dados…'
                                    : 'Sem contactos registados neste período.'}
                            </EmptyChartState>
                        )}
                    </ChartCard>
                </TabPanel>
            )}
            <TabPanel value={tabSelected} index={supplierTabIndex}>
                <CompanySuppliers
                    suppliers={suppliers ?? undefined}
                    companyId={company ? company.uuid : ''}
                    isLoading={isLoadingSuppliers}
                />
            </TabPanel>
        </ThemeProvider>
    ) : null
}

const BarList = ({ items }: { items: { label: string; count: number }[] }) => {
    const sorted = [...items].sort((a, b) => b.count - a.count)
    const max = sorted.length ? sorted[0].count : 0

    if (!sorted.length) {
        return <EmptyBreakdown>Sem dados disponíveis.</EmptyBreakdown>
    }

    return (
        <BarListContainer>
            {sorted.map((item) => (
                <BarRow key={item.label}>
                    <BarLabel title={item.label}>{item.label}</BarLabel>
                    <BarTrack>
                        <BarFill
                            style={{
                                transform: `scaleX(${max ? item.count / max : 0})`,
                            }}
                        />
                    </BarTrack>
                    <BarCount>{item.count}</BarCount>
                </BarRow>
            ))}
        </BarListContainer>
    )
}

const ContactsTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) {
        return null
    }
    const value = payload[0].value
    return (
        <TooltipBox>
            <TooltipDate>{label}</TooltipDate>
            <TooltipRow>
                <TooltipKey />
                <TooltipValue>{value}</TooltipValue>
                <TooltipUnit>
                    {value === 1 ? 'novo contacto' : 'novos contactos'}
                </TooltipUnit>
            </TooltipRow>
        </TooltipBox>
    )
}

const SearchContainer = styled(Column as any)`
    ${WrapRow} {
        > div {
            margin-bottom: 24px;
        }
        > div:not(:last-child) {
            margin-right: 24px;
        }
    }
`

const StyledInput = styled('input')`
    font-family: Merriweather-Light;
    border: 0;
    height: 36px;
    outline: 0;
    border-bottom: 1px solid ${Colors['light-grey-blue']};
    font-size: 16px;
    ${Typography['Body_Text_#2_Regular_Left']};
    ::placeholder {
        color: #8996a6;
    }
`

const FilterLabel = styled('span')`
    ${Typography['Body_Text_#3_Low_Contrast_Left']}
    margin-right: 12px;
    white-space: nowrap;
`

const GroupByGroup = styled('div')`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 3px;
    border-radius: 8px;
    background-color: ${Colors['ice-blue']};
`

const GroupByButton = styled('button')<{ active: boolean }>`
    border: 0;
    cursor: pointer;
    padding: 6px 14px;
    border-radius: 6px;
    ${Typography['Body_Text_#3_Regular_Left']}
    white-space: nowrap;
    transition:
        background-color 0.15s ease,
        color 0.15s ease,
        box-shadow 0.15s ease;
    background-color: ${(props) =>
        props.active ? Colors.white : 'transparent'};
    color: ${(props) => (props.active ? Colors.dark : Colors['slate-light'])};
    box-shadow: ${(props) =>
        props.active ? '0 1px 3px rgba(40, 61, 59, 0.16)' : 'none'};
    font-weight: ${(props) => (props.active ? 600 : 400)};
`

const StatGrid = styled('div')`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 8px;
`

const StatTile = styled('div')`
    padding: 20px 24px;
    border-radius: 12px;
    background-color: ${Colors['pale-grey']};
    border: 1px solid ${Colors['ice-blue']};
`

const StatLabel = styled('div')`
    ${Typography['Body_Text_#3_Low_Contrast_Left']}
    margin-bottom: 8px;
`

const StatValue = styled('div')`
    ${Typography.H2_Regular_Left}
    font-size: 28px;
`

const StatUnit = styled('span')`
    ${Typography['Body_Text_#3_Low_Contrast_Left']}
`

const BreakdownGrid = styled('div')`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
`

const BreakdownCard = styled('div')`
    padding: 20px 24px;
    border-radius: 12px;
    background-color: ${Colors['pale-grey']};
    border: 1px solid ${Colors['ice-blue']};
`

const BreakdownTitle = styled('h3')`
    ${Typography.H3_Regular_Left}
    font-size: 16px;
    margin: 0 0 16px 0;
`

const StackedBar = styled('div')`
    display: flex;
    width: 100%;
    height: 14px;
    border-radius: 7px;
    overflow: hidden;
    background-color: ${Colors['ice-blue']};
    gap: 2px;
`

const StackedBarSegment = styled('div')`
    height: 100%;
`

const StackedBarLegend = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
`

const LegendItem = styled('div')`
    display: flex;
    align-items: center;
`

const LegendSwatch = styled('span')`
    width: 10px;
    height: 10px;
    border-radius: 3px;
    margin-right: 10px;
    flex-shrink: 0;
`

const LegendLabel = styled('span')`
    ${Typography['Body_Text_#2_Regular_Left']}
    flex: 1;
`

const LegendCount = styled('span')`
    ${Typography['Body_Text_#1_High_Contrast_Right']}
    font-size: 14px;
`

const EmptyBreakdown = styled('div')`
    ${Typography['Body_Text_#2_Low_Contrast_Left']}
    padding: 8px 0;
`

const BarListContainer = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 360px;
    overflow-y: auto;
    padding-right: 4px;
`

const BarRow = styled('div')`
    display: flex;
    align-items: center;
    gap: 12px;
`

const BarLabel = styled('span')`
    ${Typography['Body_Text_#2_Regular_Left']}
    font-size: 14px;
    width: 120px;
    flex-shrink: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const BarTrack = styled('div')`
    flex: 1;
    height: 10px;
    border-radius: 5px;
    background-color: ${Colors['ice-blue']};
    overflow: hidden;
`

const BarFill = styled('div')`
    width: 100%;
    height: 100%;
    border-radius: 5px;
    background-color: ${ACCENT};
    transform-origin: left;
    transition: transform 0.4s ease;
`

const BarCount = styled('span')`
    ${Typography['Body_Text_#1_High_Contrast_Right']}
    font-size: 14px;
    width: 32px;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
`

const ChartCard = styled('div')`
    padding: 24px;
    border-radius: 12px;
    background-color: ${Colors['pale-grey']};
    border: 1px solid ${Colors['ice-blue']};
`

const ChartHeading = styled('h3')`
    ${Typography.H3_Regular_Left}
    font-size: 16px;
    margin: 0 0 16px 0;
`

const ChartWrapper = styled('div')`
    transition: opacity 0.2s ease;
`

const EmptyChartState = styled('div')`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 320px;
    ${Typography['Body_Text_#2_Low_Contrast_Center']}
`

const TooltipBox = styled('div')`
    background-color: ${Colors.white};
    border: 1px solid ${Colors['ice-blue']};
    border-radius: 8px;
    padding: 10px 14px;
    box-shadow: 0 4px 12px rgba(40, 61, 59, 0.12);
`

const TooltipDate = styled('div')`
    ${Typography['Body_Text_#3_Low_Contrast_Left']}
    margin-bottom: 4px;
`

const TooltipRow = styled('div')`
    display: flex;
    align-items: center;
`

const TooltipKey = styled('span')`
    display: inline-block;
    width: 10px;
    height: 2px;
    background-color: ${ACCENT};
    margin-right: 8px;
`

const TooltipValue = styled('span')`
    ${Typography['Body_Text_#1_High_Contrast_Left']}
    margin-right: 6px;
`

const TooltipUnit = styled('span')`
    ${Typography['Body_Text_#3_Low_Contrast_Left']}
`

const TabsRowLabel = styled('span')`
    ${Typography.H2_Regular_Left}
    font-size: 18px;
    padding: 4px;
    text-transform: none;
`

const DateSeparator = styled('span')`
    margin: 0px 16px;
`
